import { lazy, Suspense, useEffect, useState } from 'react';
import { loadSplatManifest, type SplatManifestEntry } from '../lib/splats';
import { useLandmarkStore } from '../state/landmarks';
import type { Landmark } from '../types/landmark';

const SplatViewer = lazy(() =>
  import('./SplatViewer').then((m) => ({ default: m.SplatViewer })),
);

interface SplatsGalleryProps {
  isOpen: boolean;
  onClose: () => void;
  records: Landmark[];
}

interface SplatRow extends SplatManifestEntry {
  landmarkName: string;
  landmark: Landmark | null;
  thumbUrl?: string;
}

export function SplatsGallery({ isOpen, onClose, records }: SplatsGalleryProps) {
  const selectLandmark = useLandmarkStore((s) => s.selectLandmark);
  const [rows, setRows] = useState<SplatRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState<SplatRow | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setError(null);
    loadSplatManifest()
      .then((manifest) => {
        if (cancelled) return;
        const enriched: SplatRow[] = manifest.entries.map((entry) => {
          const lm = records.find((r) => r.oerId === entry.oerId) ?? null;
          const thumb =
            lm && lm.tier === 'featured' ? lm.photos?.[0]?.thumbnailUrl ?? lm.photos?.[0]?.url : undefined;
          return {
            ...entry,
            landmarkName: lm?.name ?? `oer ${entry.oerId}`,
            landmark: lm,
            ...(thumb ? { thumbUrl: thumb } : {}),
          };
        });
        setRows(enriched);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, records]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !active) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose, active]);

  if (!isOpen) return null;

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="splats-gallery-title"
        data-testid="splats-gallery"
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(10, 8, 7, 0.92)',
          backdropFilter: 'blur(20px)',
          zIndex: 900,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            padding: 'var(--space-5) var(--space-6)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <div>
            <h2
              id="splats-gallery-title"
              style={{
                margin: 0,
                fontSize: '1.4rem',
                letterSpacing: '-0.02em',
                color: 'var(--text-headline)',
                fontFamily: 'var(--font-serif, ui-serif, Georgia, serif)',
              }}
            >
              3D Views
            </h2>
            <p
              style={{
                margin: '4px 0 0',
                fontSize: '0.85rem',
                color: 'var(--text-muted)',
              }}
            >
              Gaussian splats from drone & gimbal footage. Drag to orbit, scroll to zoom.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close 3D Views gallery"
            data-testid="splats-gallery-close"
            style={{
              width: 32,
              height: 32,
              borderRadius: 'var(--radius-sm)',
              background: 'transparent',
              color: 'var(--text-muted)',
              border: '1px solid var(--border)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <svg
              width={16}
              height={16}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.75}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M6 6L18 18M18 6L6 18" />
            </svg>
          </button>
        </header>

        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 'var(--space-6)',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 'var(--space-5)',
            alignContent: 'start',
          }}
        >
          {error ? (
            <p style={{ color: 'var(--error, #d4756a)' }}>Failed to load: {error}</p>
          ) : null}
          {!error && rows.length === 0 ? (
            <p style={{ color: 'var(--text-muted)' }}>No 3D views available yet.</p>
          ) : null}
          {rows.map((row) => (
            <article
              key={row.oerId}
              style={{
                background: 'var(--surface-raised)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <button
                type="button"
                onClick={() => setActive(row)}
                data-testid={`splats-gallery-open-${row.oerId}`}
                style={{
                  display: 'block',
                  width: '100%',
                  aspectRatio: '4 / 3',
                  background: row.thumbUrl
                    ? `center/cover no-repeat url(${row.thumbUrl})`
                    : 'var(--surface)',
                  border: 'none',
                  borderBottom: '1px solid var(--border)',
                  cursor: 'pointer',
                  position: 'relative',
                  padding: 0,
                }}
                aria-label={`Open 3D view of ${row.landmarkName}`}
              >
                <span
                  style={{
                    position: 'absolute',
                    bottom: 'var(--space-3)',
                    left: 'var(--space-3)',
                    padding: '4px 8px',
                    background: 'rgba(20, 16, 12, 0.85)',
                    color: 'var(--text-headline)',
                    fontSize: '0.7rem',
                    fontFamily: 'var(--font-mono, ui-monospace, monospace)',
                    borderRadius: 'var(--radius-sm)',
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                  }}
                >
                  3D · {(row.bytes / 1_048_576).toFixed(0)} MB
                </span>
              </button>
              <div style={{ padding: 'var(--space-3) var(--space-4) var(--space-4)' }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: '1rem',
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    color: 'var(--text-headline)',
                  }}
                >
                  {row.landmarkName}
                </h3>
                <p
                  style={{
                    margin: '4px 0 var(--space-3)',
                    fontSize: '0.75rem',
                    color: 'var(--text-muted)',
                    fontFamily: 'var(--font-mono, ui-monospace, monospace)',
                  }}
                >
                  {row.vertexCount.toLocaleString()} splats · captured {row.capturedAt}
                </p>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  <button
                    type="button"
                    onClick={() => setActive(row)}
                    style={{
                      flex: 1,
                      padding: '6px 10px',
                      background: 'var(--primary)',
                      color: '#fffdf2',
                      border: '1px solid var(--primary)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Open 3D view
                  </button>
                  {row.landmark ? (
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        selectLandmark(row.landmark);
                      }}
                      style={{
                        padding: '6px 10px',
                        background: 'var(--surface)',
                        color: 'var(--text)',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                      }}
                    >
                      Details
                    </button>
                  ) : null}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      {active ? (
        <Suspense fallback={null}>
          <SplatViewer
            splat={active}
            landmarkName={active.landmarkName}
            onClose={() => setActive(null)}
          />
        </Suspense>
      ) : null}
    </>
  );
}
